/**
 * Upsell card behavior for snippets/product-upsell-card.liquid.
 *
 * Choosing a variant updates the add form's variant id, the price (through the temporary sale
 * resolver when present), the card image, the cart-form product data and a polite status. The
 * "What's Included" accordion opens and closes. Connecting is idempotent, and every listener lives
 * on the card's own elements, so a card removed from the page takes its listeners with it.
 */

const CARD_SELECTOR = '[data-upsell-card]';
const READY = 'data-upsell-card-ready';

function resolvePricing(variant) {
  const resolver = window.YISETemporarySalePrice;
  if (!resolver || typeof resolver.resolveVariantPrice !== 'function') return null;
  return resolver.resolveVariantPrice({
    price: variant.price,
    price_cents: variant.price_cents,
    compareAtPrice: variant.compare_at_price || '',
    compare_at_price_cents: variant.compare_at_price_cents,
    temporarySalePrice: variant.temporarySalePrice,
  });
}

function connectAccordion(card) {
  card.querySelectorAll('[data-upsell-card-accordion]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const content = card.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
      trigger.setAttribute('aria-expanded', String(!expanded));
      if (!content) return;
      content.setAttribute('aria-hidden', String(expanded));
      content.style.maxHeight = expanded ? '0' : `${content.scrollHeight}px`;
    });
  });
}

function connectVariants(card) {
  const data = card.querySelector('script[data-upsell-card-variants]');
  const form = card.querySelector('form[data-upsell-card-form]');
  if (!data || !form) return;

  let variants = [];
  try {
    variants = JSON.parse(data.textContent);
  } catch (error) {
    return;
  }

  const idInput = form.querySelector('input[name="id"]');
  const price = form.querySelector('.js-price');
  const submit = form.querySelector('button[type="submit"]');
  const cartLineImage = form.querySelector('template[data-cart-line-media]')?.content?.querySelector('img');
  const image = card.querySelector('.pdp-cross-sell__image');
  const status = card.querySelector('[data-upsell-card-status]');
  const selects = card.querySelectorAll('.pdp-cross-sell__variant-select');
  const swatches = card.querySelectorAll('.pdp-cross-sell__swatch-input');
  const title = (card.querySelector('.pdp-cross-sell__title') || {}).textContent || '';

  const selectedOptions = () => {
    const options = [];
    card.querySelectorAll('.pdp-cross-sell__swatch-group').forEach((group) => {
      const checked = group.querySelector('.pdp-cross-sell__swatch-input:checked');
      options[Number(group.getAttribute('data-option-index'))] = checked ? checked.value : null;
    });
    selects.forEach((select) => {
      options[Number(select.getAttribute('data-option-index'))] = select.value;
    });
    return options;
  };

  const matchingVariant = () => {
    const options = selectedOptions();
    return variants.find((variant) => [variant.option1, variant.option2, variant.option3]
      .every((value, index) => !options[index] || value === options[index]));
  };

  const update = (announce) => {
    const variant = matchingVariant();
    if (!variant) return;

    if (idInput) idInput.value = variant.id;
    if (submit) {
      submit.disabled = !variant.available;
      submit.setAttribute('aria-disabled', String(!variant.available));
    }
    if (submit && submit.hasAttribute('data-click-variantid')) {
      submit.setAttribute('data-click-variantid', variant.id);
      submit.setAttribute('data-click-variant', variant.title);
    }

    const pricing = resolvePricing(variant);
    const displayPrice = (pricing && pricing.priceFormatted) || variant.price;
    const priceCents = (pricing && pricing.price) ?? variant.price_cents;
    form.dataset.variantPrice = String(priceCents);
    form.dataset.cartLineSubtitle = variant.title === 'Default Title' ? '' : variant.title;
    if (variant.product_url) form.dataset.productUrl = variant.product_url;
    if (cartLineImage && variant.cart_image) {
      cartLineImage.setAttribute('src', variant.cart_image);
      cartLineImage.removeAttribute('srcset');
      cartLineImage.removeAttribute('sizes');
      cartLineImage.setAttribute('alt', variant.cart_image_alt || title.trim());
    }
    if (price) {
      const compare = (pricing && pricing.compareAtPriceFormatted) || variant.compare_at_price || null;
      const label = compare ? card.dataset.labelSalePrice : card.dataset.labelPrice;
      let markup = `<span class="visually-hidden">${label} </span>${String(displayPrice).replace('.00', '')}`;
      if (compare) {
        markup += `<span class="visually-hidden">${card.dataset.labelRegularPrice} </span> <span class="pdp-item__price-strikethrough" style="text-decoration: line-through;opacity:0.5;">${String(compare).replace('.00', '')}</span>`;
      }
      price.innerHTML = markup;
    }

    if (image && variant.featured_image) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = variant.featured_image;
    }

    const cartData = form.querySelector('script.cart-form-product-data');
    if (cartData) {
      try {
        const payload = JSON.parse(cartData.textContent);
        payload.variant_id = variant.id;
        payload.price = (pricing && pricing.price) || variant.price_cents;
        payload.compare_at_price = (pricing && pricing.compareAtPrice) || null;
        payload.temporary_sale_price = variant.temporarySalePrice || null;
        payload.variant_title = variant.title === 'Default Title' ? '' : variant.title;
        if (variant.featured_image) payload.image = variant.featured_image;
        if (variant.product_url) payload.url = variant.product_url;
        payload.image_alt = variant.cart_image_alt || title.trim();
        cartData.textContent = JSON.stringify(payload);
      } catch (error) {
        /* Leave the server-rendered payload in place if it cannot be read. */
      }
    }

    if (announce && status) {
      const variantTitle = variant.title && variant.title !== 'Default Title' ? `${variant.title}. ` : '';
      status.textContent = `${title.trim()}. ${variantTitle}${String(displayPrice)}. ${variant.available ? 'Available.' : 'Unavailable.'}`;
    }
  };

  selects.forEach((select) => select.addEventListener('change', () => update(true)));
  swatches.forEach((input) => {
    input.addEventListener('change', () => {
      const label = card.querySelector(`[data-upsell-card-selected="${input.getAttribute('data-option-index')}"]`);
      if (label) label.textContent = input.value;
      update(true);
    });
  });
  update(false);
}

function connectCard(card) {
  if (card.hasAttribute(READY)) return;
  card.setAttribute(READY, '');
  connectAccordion(card);
  connectVariants(card);
}

export function connect(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  if (root instanceof Element && root.matches(CARD_SELECTOR)) connectCard(root);
  root.querySelectorAll(CARD_SELECTOR).forEach(connectCard);
}

window.YiseProductUpsellCard = { connect };

connect(document);
document.addEventListener('shopify:section:load', (event) => connect(event.target));
